import {
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
  DOCTOR_VISIT_STATUS_PENDING,
  DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
  DOCTOR_VISIT_STATUS_READY,
  VISIT_TYPE_GENERALIZED_ANXIETY,
  VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP,
  VISIT_TYPE_INSOMNIA,
  VISIT_TYPE_INSOMNIA_FOLLOW_UP,
  VISIT_TYPE_PERFORMANCE_ANXIETY,
  VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
} from "~/shared/sharedConstants";
import {
  getEditPendingVisitExpiresAt,
  getIsEditingPendingVisit,
} from "~/visits/utils/visitCookies";

export const PENDING_VISIT_EDIT_TTL_MS = 15 * 60 * 1000;

const VISIT_TYPE_TO_CONSULTATION_TYPE_ABBR = {
  [VISIT_TYPE_PERFORMANCE_ANXIETY]:
    CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  [VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP]:
    CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
  [VISIT_TYPE_GENERALIZED_ANXIETY]:
    CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY,
  [VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP]:
    CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP,
  [VISIT_TYPE_INSOMNIA]: CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
  [VISIT_TYPE_INSOMNIA_FOLLOW_UP]:
    CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
};

const CONSULTATION_TYPE_ABBR_TO_VISIT_TYPE = {
  [CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY]:
    VISIT_TYPE_PERFORMANCE_ANXIETY,
  [CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP]:
    VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
  [CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY]:
    VISIT_TYPE_GENERALIZED_ANXIETY,
  [CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP]:
    VISIT_TYPE_GENERALIZED_ANXIETY_FOLLOW_UP,
  [CONSULTATION_TYPE_ABBREVIATION_INSOMNIA]: VISIT_TYPE_INSOMNIA,
  [CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP]:
    VISIT_TYPE_INSOMNIA_FOLLOW_UP,
};

// Statuses considered "pending" from the patient's perspective — anything where the visit has
// been submitted but the final prescription isn't out the door yet. PRAD (Pending Review Admin)
// is included because the patient still sees these in the "Thanks for completing your visit"
// PendingVisitCard and the editability rules should match that UI grouping.
const PENDING_STATUSES = [
  DOCTOR_VISIT_STATUS_PENDING,
  DOCTOR_VISIT_STATUS_READY,
  DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
];

export function consultationTypeForVisitType(visitType) {
  return VISIT_TYPE_TO_CONSULTATION_TYPE_ABBR[visitType] || "";
}

export function visitTypeForConsultationType(consultationType) {
  return CONSULTATION_TYPE_ABBR_TO_VISIT_TYPE[consultationType] || "";
}

export function getPendingVisitOfType(visits, visitType) {
  if (!visits || !visitType) return null;
  return (
    visits.find(
      (v) => v.visitType === visitType && PENDING_STATUSES.includes(v.status)
    ) || null
  );
}

export function getAnyPendingVisit(visits) {
  if (!visits) return null;
  return visits.find((v) => PENDING_STATUSES.includes(v.status)) || null;
}

// buildPendingVisitsByType returns a map of visit_type → array of visit IDs for visits in any
// "pending" status (P, R, PRAD). Iteration order follows the input order (visits are sorted
// short_id DESC by the GraphQL resolver, so byType[type][0] is the most recent pending visit
// of that type).
export function buildPendingVisitsByType(visits) {
  const byType = {};
  if (!visits) return byType;
  for (const v of visits) {
    if (PENDING_STATUSES.includes(v.status)) {
      if (!byType[v.visitType]) byType[v.visitType] = [];
      byType[v.visitType].push(v.id);
    }
  }
  return byType;
}

export function hasPendingVisitOfType(pendingByType, visitType) {
  return !!(pendingByType && pendingByType[visitType]?.length);
}

export function isVisitPendingPrimary(pendingByType, visit) {
  if (!pendingByType || !visit) return false;
  const ids = pendingByType[visit.visitType];
  return !!(ids && ids[0] === visit.id);
}

// A visit's order item is "flavored" when its subcategory field is set OR its productSlug contains
// "flavored". The subcategory carries the chosen flavor (e.g. "tangerine"); productSlug guards
// against the rare case where the subcategory hasn't been chosen yet.
export function hasFlavoredOrderItem(visit) {
  if (!visit || !visit.orderItems) return false;
  return visit.orderItems.some(
    (oi) =>
      (oi.subcategory && oi.subcategory.length > 0) ||
      (oi.productSlug && oi.productSlug.indexOf("flavored") !== -1)
  );
}

export function isInPendingEditFlow(checkoutSessionID) {
  if (!checkoutSessionID) return false;
  return getIsEditingPendingVisit(checkoutSessionID);
}

export function isPendingEditExpired(checkoutSessionID) {
  const expiresAt = getEditPendingVisitExpiresAt(checkoutSessionID);
  if (!expiresAt) return false;
  return Date.now() >= expiresAt;
}

// editPendingPharmacyError returns a user-facing message when a pending-visit edit would submit a
// pharmacy-pickup (visit-only) order without a selected pharmacy, or "" when the submission is OK.
// It mirrors the server-side guard (ValidateEditPendingPayload) so the patient gets immediate
// feedback instead of a 400. A pharmacy-pickup order is one whose order type contains "visit-only"
// (matching isPharmacyPickupOrderType in sharedUtils and IsVisitOnlyOrderType on the Go side).
export function editPendingPharmacyError(orderType, pharmacyJSON) {
  const isPickup =
    typeof orderType === "string" && orderType.includes("visit-only");
  if (!isPickup) return "";
  const hasPharmacy =
    !!pharmacyJSON &&
    typeof pharmacyJSON === "object" &&
    typeof pharmacyJSON.pharmacyName === "string" &&
    pharmacyJSON.pharmacyName.trim().length > 0;
  return hasPharmacy
    ? ""
    : "Please select a pharmacy before continuing for pharmacy-pickup orders.";
}
